<template>
  <div class="cp-wrap">
    <div class="cp-head">
      <div>
        <h2 class="cp-title">Client Partners</h2>
        <p class="cp-sub">View-only partners tied to one vendor. They own a set of that vendor's clients and see only those clients' pay-in/pay-out activity + client commission.</p>
      </div>
      <button class="cp-btn cp-btn-primary" @click="openCreate">+ Add Client Partner</button>
    </div>

    <div v-if="loading" class="cp-empty">Loading…</div>
    <div v-else-if="!partners.length" class="cp-empty">No client partners yet.</div>
    <div v-else class="cp-list">
      <div v-for="p in partners" :key="p.id" class="cp-card" :class="{ 'is-inactive': !p.status }">
        <div class="cp-card-main">
          <div class="cp-card-name">{{ p.name || p.username }}
            <span :class="['cp-badge', p.status ? 'cp-badge-on' : 'cp-badge-off']">{{ p.status ? 'Login allowed' : 'Login stopped' }}</span>
          </div>
          <div class="cp-card-meta">@{{ p.username }} · vendor <b>{{ p.vendor }}</b><span v-if="p.email"> · {{ p.email }}</span></div>
        </div>
        <div class="cp-card-actions">
          <button class="cp-btn" @click="openEdit(p)">Edit</button>
          <button v-if="p.status" class="cp-btn cp-btn-warn" @click="setStatus(p, 0)">Stop login</button>
          <button v-else class="cp-btn cp-btn-primary" @click="setStatus(p, 1)">Allow login</button>
        </div>
      </div>
    </div>

    <div v-if="showForm" class="cp-modal-backdrop" @click.self="showForm = false">
      <div class="cp-modal">
        <div class="cp-modal-head">
          <h3>{{ editing ? 'Edit Client Partner' : 'Add Client Partner' }}</h3>
          <button class="cp-x" @click="showForm = false">×</button>
        </div>
        <div class="cp-modal-body">
          <div class="cp-grid">
            <div class="cp-field">
              <label>Vendor</label>
              <select v-model="form.vendor" class="cp-input" :disabled="!!editing">
                <option value="">Select a vendor</option>
                <option v-for="v in vendors" :key="v.username" :value="v.username">{{ v.username }}</option>
              </select>
            </div>
            <div class="cp-field">
              <label>Name</label>
              <input v-model.trim="form.name" class="cp-input" placeholder="Full name" />
            </div>
            <div class="cp-field">
              <label>Username</label>
              <input v-model.trim="form.username" class="cp-input" :disabled="!!editing" placeholder="login username" />
            </div>
            <div class="cp-field">
              <label>{{ editing ? 'Reset password (optional)' : 'Password' }}</label>
              <input v-model.trim="form.password" type="password" class="cp-input" placeholder="••••••" />
            </div>
            <div class="cp-field">
              <label>Email</label>
              <input v-model.trim="form.email" class="cp-input" placeholder="email (optional)" />
            </div>
            <div class="cp-field">
              <label>Phone</label>
              <input v-model.trim="form.phone" class="cp-input" placeholder="phone (optional)" />
            </div>
            <div class="cp-field" v-if="editing">
              <label>Login access</label>
              <select v-model="form.status" class="cp-input">
                <option :value="1">Allowed — partner can log in</option>
                <option :value="0">Stopped — partner cannot log in</option>
              </select>
            </div>
          </div>

          <!-- Client selection: assign which of the vendor's clients this partner owns -->
          <div class="cp-clients">
            <div class="cp-clients-head">
              <label>Clients owned by this partner</label>
              <input v-if="form.vendor" v-model.trim="clientFilter" class="cp-input cp-input-sm" placeholder="Filter clients…" />
            </div>
            <p v-if="!form.vendor" class="cp-hint">Select a vendor first to assign clients.</p>
            <div v-else-if="clientsLoading" class="cp-hint">Loading clients…</div>
            <div v-else-if="!filteredClients.length" class="cp-hint">No clients found for this vendor.</div>
            <div v-else class="cp-clients-list">
              <label
                v-for="c in filteredClients"
                :key="c.clientName"
                class="cp-client-row"
                :class="{ 'is-disabled': !c.selectable }"
                :title="c.selectable ? '' : ('Owned by ' + (c.ownerName || 'another partner'))"
              >
                <input
                  type="checkbox"
                  :disabled="!c.selectable"
                  :checked="selectedClients.includes(c.clientName)"
                  @change="toggleClient(c)"
                />
                <span class="cp-client-name">{{ c.clientName }}</span>
                <span v-if="!c.selectable" class="cp-owned-tag">owned by {{ c.ownerName || 'another partner' }}</span>
              </label>
            </div>
            <p v-if="form.vendor && !clientsLoading" class="cp-selected-count">{{ selectedClients.length }} selected</p>
          </div>

          <p v-if="formError" class="cp-error">{{ formError }}</p>
        </div>
        <div class="cp-modal-foot">
          <button class="cp-btn" @click="showForm = false">Cancel</button>
          <button class="cp-btn cp-btn-primary" :disabled="saving" @click="submit">{{ saving ? 'Saving…' : (editing ? 'Save changes' : 'Create') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../../http-common";

export default {
  name: "client-partners",
  data() {
    return {
      loading: true,
      partners: [],
      vendors: [],
      showForm: false,
      editing: null,
      saving: false,
      formError: "",
      form: { username: "", password: "", name: "", email: "", phone: "", vendor: "", status: 1 },
      // Client selection
      clients: [],
      selectedClients: [],
      clientsLoading: false,
      clientFilter: "",
    };
  },
  computed: {
    filteredClients() {
      const q = this.clientFilter.toLowerCase();
      if (!q) return this.clients;
      return this.clients.filter((c) => c.clientName.toLowerCase().includes(q));
    },
  },
  watch: {
    // Re-load the vendor's clients whenever the vendor changes. On a vendor
    // switch the previous selection no longer applies (clients are per-vendor).
    "form.vendor"(v, old) {
      if (v && v !== old) {
        if (old !== undefined) this.selectedClients = [];
        this.loadClients();
      }
    },
  },
  async mounted() {
    await Promise.all([this.loadPartners(), this.loadVendors()]);
  },
  methods: {
    async loadPartners() {
      this.loading = true;
      try {
        const r = await http.get("/client-partners");
        this.partners = (r.data && r.data.data) || [];
      } catch (e) { /* ignore */ }
      this.loading = false;
    },
    async loadVendors() {
      try {
        const r = await http.get("/vendor");
        this.vendors = (r.data && (r.data.data || r.data)) || [];
      } catch (e) { this.vendors = []; }
    },
    async loadClients() {
      if (!this.form.vendor) { this.clients = []; return; }
      this.clientsLoading = true;
      try {
        const partnerId = this.editing ? this.editing.id : "";
        const r = await http.get(`/client-partners/assignable-clients?vendor=${encodeURIComponent(this.form.vendor)}&partnerId=${partnerId}`);
        this.clients = (r.data && r.data.data) || [];
        // Pre-select the clients already owned by THIS partner (edit flow).
        const owned = this.clients.filter((c) => c.ownedByThis).map((c) => c.clientName);
        // Keep any manual selections the operator already made this session.
        const merged = new Set([...this.selectedClients, ...owned]);
        // Drop any selection that is no longer selectable (owned elsewhere).
        const selectable = new Set(this.clients.filter((c) => c.selectable).map((c) => c.clientName));
        this.selectedClients = [...merged].filter((n) => selectable.has(n));
      } catch (e) {
        this.clients = [];
      }
      this.clientsLoading = false;
    },
    toggleClient(c) {
      if (!c.selectable) return;
      const i = this.selectedClients.indexOf(c.clientName);
      if (i === -1) this.selectedClients.push(c.clientName);
      else this.selectedClients.splice(i, 1);
    },
    openCreate() {
      this.editing = null;
      this.form = { username: "", password: "", name: "", email: "", phone: "", vendor: "", status: 1 };
      this.selectedClients = [];
      this.clients = [];
      this.clientFilter = "";
      this.formError = "";
      this.showForm = true;
    },
    openEdit(p) {
      this.editing = p;
      this.form = { username: p.username, password: "", name: p.name || "", email: p.email || "", phone: p.phone || "", vendor: p.vendor, status: p.status };
      this.selectedClients = [];
      this.clients = [];
      this.clientFilter = "";
      this.formError = "";
      this.showForm = true;
      this.loadClients();
    },
    async submit() {
      this.formError = "";
      if (!this.editing && (!this.form.username || !this.form.password || !this.form.vendor)) {
        this.formError = "Username, password and vendor are required."; return;
      }
      this.saving = true;
      try {
        if (this.editing) {
          const body = {
            name: this.form.name, email: this.form.email, phone: this.form.phone,
            status: this.form.status, clientNames: this.selectedClients,
          };
          if (this.form.password) body.password = this.form.password;
          await http.put(`/client-partners/${this.editing.username}`, body);
        } else {
          await http.post("/client-partners", { ...this.form, clientNames: this.selectedClients });
        }
        this.showForm = false;
        await this.loadPartners();
      } catch (e) {
        this.formError = (e.response && e.response.data && e.response.data.message) || "Save failed.";
      }
      this.saving = false;
    },
    async setStatus(p, status) {
      try { await http.put(`/client-partners/${p.username}`, { status }); await this.loadPartners(); } catch (e) { /* ignore */ }
    },
  },
};
</script>

<style scoped>
.cp-wrap { padding: 20px 22px; }
.cp-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; margin-bottom: 22px; padding-bottom: 18px; border-bottom: 1px solid #eef0f5; }
.cp-title { font-size: 20px; font-weight: 700; color: #1b2a47; margin: 0; }
.cp-sub { color: #6b7280; font-size: 13px; margin: 6px 0 0; max-width: 620px; line-height: 1.5; }
.cp-btn { border: 1px solid #d7dbe6; background: #fff; color: #1b2a47; border-radius: 10px; padding: 9px 16px; font-weight: 600; cursor: pointer; font-size: 13px; }
.cp-btn:hover { border-color: #b8bfd0; }
.cp-btn-primary { background: #00296f; border-color: #00296f; color: #fff; }
.cp-btn-warn { color: #b45309; border-color: #f3d9a8; }
.cp-empty { padding: 40px; text-align: center; color: #8a92a6; background: #f8f9fc; border-radius: 12px; }
.cp-list { display: flex; flex-direction: column; gap: 10px; }
.cp-card { display: flex; justify-content: space-between; align-items: center; gap: 14px; background: #fff; border: 1px solid #eef0f5; border-radius: 12px; padding: 16px 18px; }
.cp-card.is-inactive { background: #fcfcfd; }
.cp-card-name { font-weight: 700; color: #1b2a47; display: flex; align-items: center; gap: 8px; }
.cp-card-meta { color: #8a92a6; font-size: 12px; margin-top: 3px; }
.cp-card-actions { display: flex; gap: 8px; flex-shrink: 0; }
.cp-badge { font-size: 10px; padding: 2px 8px; border-radius: 999px; font-weight: 700; }
.cp-badge-on { background: #e6f6ec; color: #1a8b48; }
.cp-badge-off { background: #fdecec; color: #cb3a47; }
.cp-modal-backdrop { position: fixed; inset: 0; background: rgba(16,22,41,.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
.cp-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 640px; max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; }
.cp-modal-head { display: flex; justify-content: space-between; align-items: center; padding: 18px 22px; border-bottom: 1px solid #eef0f5; }
.cp-modal-head h3 { margin: 0; font-size: 17px; color: #1b2a47; }
.cp-x { border: none; background: none; font-size: 26px; line-height: 1; color: #9aa1b2; cursor: pointer; }
.cp-modal-body { padding: 20px 22px; overflow-y: auto; }
.cp-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.cp-field { display: flex; flex-direction: column; gap: 5px; }
.cp-field label { font-size: 12px; font-weight: 600; color: #465066; }
.cp-input { height: 40px; padding: 8px 12px; border: 1px solid #d7dbe6; border-radius: 9px; background: #fff; color: #1b2a47; font-size: 14px; outline: none; box-sizing: border-box; }
.cp-input:focus { border-color: #00296f; box-shadow: 0 0 0 2px rgba(0,41,111,.12); }
.cp-input:disabled { background: #f4f6fa; color: #8a92a6; }
.cp-input-sm { height: 34px; width: 200px; font-size: 13px; }
.cp-clients { margin-top: 18px; padding-top: 16px; border-top: 1px solid #eef0f5; }
.cp-clients-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 10px; }
.cp-clients-head label { font-size: 12px; font-weight: 600; color: #465066; }
.cp-hint { color: #8a92a6; font-size: 13px; margin: 6px 0; }
.cp-clients-list { max-height: 240px; overflow-y: auto; border: 1px solid #eef0f5; border-radius: 10px; }
.cp-client-row { display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-bottom: 1px solid #f4f6fa; cursor: pointer; font-size: 14px; color: #1b2a47; }
.cp-client-row:last-child { border-bottom: none; }
.cp-client-row.is-disabled { cursor: not-allowed; color: #a7adbd; background: #fafbfd; }
.cp-client-row input[type="checkbox"] { width: 16px; height: 16px; cursor: inherit; }
.cp-client-name { flex: 1; }
.cp-owned-tag { font-size: 10px; font-weight: 700; color: #b45309; background: #fdf2e2; padding: 2px 8px; border-radius: 999px; }
.cp-selected-count { font-size: 12px; color: #6b7280; margin: 8px 0 0; }
.cp-error { color: #cb3a47; font-size: 13px; margin-top: 12px; }
.cp-modal-foot { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 22px; border-top: 1px solid #eef0f5; }
@media (max-width: 640px) { .cp-grid { grid-template-columns: 1fr; } .cp-input-sm { width: 140px; } }
</style>
